/**
 * Le retour arrière d'Android et Échap sur le web appellent `onEscape` : ce qu'un calque se ferme
 * (`<Overlay>`) ou ce qu'une vue plein cadre rend (`<StudentLivePage>`). Rend de quoi détacher les
 * deux, à appeler au nettoyage de l'effet qui l'a posé.
 *
 * **Seul le dernier calque qui écoute répond**, comme le retour arrière d'Android (`BackHandler`
 * n'appelle que le dernier inscrit qui rend `true`) : Échap ferme la liste d'un champ ouverte
 * par-dessus « Plus d'options », pas les deux d'un coup. Les calques s'empilent dans l'ordre où ils
 * s'ouvrent. Un calque qu'on ne ferme pas d'un geste (`<Overlay dismissable={false}>`, une boîte de
 * confirmation) garde Échap sans rien faire (`holdEscape`) : la vue du dessous ne se ferme pas sous lui.
 */
import { BackHandler, Platform } from "react-native"

type WebDocument = {
    addEventListener: (type: string, listener: (event: { key?: string }) => void, capture?: boolean) => void
    removeEventListener: (type: string, listener: (event: { key?: string }) => void, capture?: boolean) => void
}

/** Les calques qui écoutent Échap au web, du plus ancien au plus récent : seul le dernier répond. */
const escapeStack: Array<() => void> = []

const onWebKeyDown = (event: { key?: string }) => {
    if (event.key === "Escape") escapeStack[escapeStack.length - 1]?.()
}

/** Pose une entrée en haut de la pile d'Échap du web ; rend de quoi la retirer. Rien hors du web. */
function pushWebEntry(onEscape: () => void): () => void {
    const doc = Platform.OS === "web" ? (globalThis as { document?: WebDocument }).document : undefined
    if (doc == null) return () => undefined
    // Une fonction par inscription : deux calques qui passent la même fonction restent deux entrées.
    const entry = () => onEscape()
    // À la capture : un champ de react-native-web arrête la touche avant qu'elle ne remonte au document.
    if (escapeStack.length === 0) doc.addEventListener("keydown", onWebKeyDown, true)
    escapeStack.push(entry)
    return () => {
        const index = escapeStack.indexOf(entry)
        if (index >= 0) escapeStack.splice(index, 1)
        if (escapeStack.length === 0) doc.removeEventListener("keydown", onWebKeyDown, true)
    }
}

export function listenEscape(onEscape: () => void): () => void {
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
        onEscape()
        return true
    })
    const removeWeb = pushWebEntry(onEscape)
    return () => {
        sub.remove()
        removeWeb()
    }
}

/**
 * Au web, garde Échap pour un calque qui ne se ferme pas d'un geste : tant qu'il est ouvert, la touche ne fait rien,
 * au lieu de fermer ce qui est dessous. Le retour arrière d'Android n'est pas pris : il garde son chemin d'avant.
 */
export function holdEscape(): () => void {
    return pushWebEntry(() => undefined)
}
